import React, { useState, useEffect, useCallback } from 'react';

import {
  TrendingUp,
  TrendingDown,
  Users,
  BookOpen,
  AlertCircle,
  CheckCircle,
  Calendar,
  Brain,
  Target,
  BarChart3,
} from 'lucide-react';

import { parentPortalApi, DashboardData } from '../services/parentPortalApi';

import { StudentEngagementChart } from './charts/StudentEngagementChart';
import { ConferencePreparation } from './ConferencePreparation';
import { EarlyWarningAlerts } from './EarlyWarningAlerts';
import { LearningRecommendations } from './LearningRecommendations';
import { TeacherCompatibilityCard } from './TeacherCompatibilityCard';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

interface Student {
  id: number;
  name: string;
  grade: string;
  school: string;
}

interface ParentDashboardProps {
  student: Student;
  onStudentChange?: (studentId: number) => void;
  availableStudents?: Student[];
}

export const ParentDashboard: React.FC<ParentDashboardProps> = ({
  student,
  onStudentChange,
  availableStudents = [],
}) => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState('30');
  const [engagementChartData, setEngagementChartData] = useState<
    Array<{ date: string; engagement: number; participation: number; focus: number }>
  >([]);

  const formatLearningStyle = (style: string | null) => {
    if (!style) {
      return 'Not available yet';
    }
    return style.replace(/_/g, ' ');
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const periodDays = parseInt(selectedTimeframe, 10);
      const data = await parentPortalApi.getDashboard(student.id, periodDays);
      setDashboardData(data);

      const weeklyScores = data.student_engagement.weekly_scores ?? [];
      const participation = data.student_engagement.participation_rate;
      const focusMinutes = data.student_engagement.focus_duration_minutes ?? 0;
      setEngagementChartData(
        weeklyScores.map((score, index) => ({
          date: `Week ${index + 1}`,
          engagement: score,
          participation,
          focus: Math.min(100, Math.round(focusMinutes * 3)),
        }))
      );
    } catch (err) {
      console.error('Failed to load parent dashboard:', err);
      setDashboardData(null);
      setEngagementChartData([]);
      setError(
        'Student insights could not be loaded. Please verify this student is linked to your account and try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [selectedTimeframe, student.id]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'declining':
        return <TrendingDown className="h-4 w-4 text-red-600" />;
      default:
        return <BarChart3 className="h-4 w-4 text-blue-600" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'destructive';
      case 'high':
        return 'destructive';
      case 'medium':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            <p>{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => void fetchDashboardData()}
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!dashboardData) {
    return null;
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {student.name}'s Learning Dashboard
          </h1>
          <p className="text-gray-600">
            {student.grade} • {student.school} • Last updated:{' '}
            {new Date().toLocaleDateString()}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={selectedTimeframe}
            onChange={e => setSelectedTimeframe(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 3 months</option>
          </select>

          {availableStudents.length > 1 && (
            <select
              value={student.id}
              onChange={e => onStudentChange?.(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableStudents.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Overall Engagement
            </CardTitle>
            <Brain className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <div className="text-2xl font-bold">
                {dashboardData.student_engagement.current_score}%
              </div>
              {getTrendIcon(dashboardData.student_engagement.trend)}
            </div>
            <Progress
              value={dashboardData.student_engagement.current_score}
              className="mt-2"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {dashboardData.student_engagement.trend === 'improving'
                ? 'Improving'
                : dashboardData.student_engagement.trend === 'declining'
                  ? 'Needs attention'
                  : 'Stable'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Learning Style
            </CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">
              {formatLearningStyle(
                dashboardData.student_engagement.primary_learning_style
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Primary learning preference
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Focus Duration
            </CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {dashboardData.student_engagement.focus_duration_minutes !== null
                ? `${Math.round(dashboardData.student_engagement.focus_duration_minutes)} min`
                : 'Not available yet'}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Average attention span
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Participation</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round(dashboardData.student_engagement.participation_rate)}%
            </div>
            <Progress
              value={dashboardData.student_engagement.participation_rate}
              className="mt-2"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Class participation rate
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Alert Summary */}
      {dashboardData.recent_alerts.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              Active Alerts ({dashboardData.recent_alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dashboardData.recent_alerts.slice(0, 2).map(alert => (
                <div
                  key={alert.alert_id}
                  className="flex items-center justify-between p-3 bg-white rounded-md border"
                >
                  <div className="flex items-center gap-3">
                    <Badge
                      variant={
                        getSeverityColor(alert.severity) as
                          'default' | 'secondary' | 'destructive'
                      }
                    >
                      {alert.severity}
                    </Badge>
                    <span className="font-medium">{alert.title}</span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {new Date(alert.trigger_date).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="teachers">Teachers</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="conferences">Conferences</TabsTrigger>
          <TabsTrigger value="insights">Insights</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Engagement Chart */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle>Engagement Trends</CardTitle>
                <CardDescription>
                  Track your child's classroom engagement over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <StudentEngagementChart
                  studentId={student.id}
                  timeframe={selectedTimeframe}
                  data={engagementChartData}
                />
              </CardContent>
            </Card>

            {/* Learning Recommendations */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle>Learning Recommendations</CardTitle>
                <CardDescription>
                  Personalized suggestions to support your child's learning
                </CardDescription>
              </CardHeader>
              <CardContent>
                <LearningRecommendations
                  recommendations={dashboardData.learning_recommendations}
                />
              </CardContent>
            </Card>
          </div>

          {/* Progress Indicators */}
          <Card>
            <CardHeader>
              <CardTitle>Progress & Goals</CardTitle>
              <CardDescription>
                Current progress and upcoming milestones
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center gap-3">
                  {dashboardData.progress_indicators.goals_on_track ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-orange-600" />
                  )}
                  <div>
                    <p className="font-medium">Goals Status</p>
                    <p className="text-sm text-gray-600">
                      {dashboardData.progress_indicators.goals_on_track
                        ? 'On Track'
                        : 'Needs Attention'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium">Next Review</p>
                    <p className="text-sm text-gray-600">
                      {new Date(
                        dashboardData.progress_indicators.next_review_date
                      ).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium">Success Factors</p>
                    <p className="text-sm text-gray-600">
                      {dashboardData.progress_indicators.success_factors.length}{' '}
                      identified
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Target className="h-5 w-5 text-orange-600" />
                  <div>
                    <p className="font-medium">Focus Areas</p>
                    <p className="text-sm text-gray-600">
                      {
                        dashboardData.progress_indicators.improvement_areas
                          .length
                      }{' '}
                      areas
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="teachers" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {dashboardData.teacher_insights.map(teacher => (
              <TeacherCompatibilityCard
                key={teacher.teacher_id}
                teacher={{
                  teacher_id: teacher.teacher_id,
                  teacher_name: teacher.teacher_name,
                  subject: teacher.subject_area,
                  effectiveness_score: teacher.overall_effectiveness,
                  compatibility_score: teacher.compatibility_score || 0,
                  key_strengths: teacher.teaching_strengths,
                }}
                studentId={student.id}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-6">
          <EarlyWarningAlerts
            studentId={student.id}
            alerts={dashboardData.recent_alerts.map(alert => ({
              id: alert.alert_id,
              type: alert.alert_type,
              severity: alert.severity as
                'low' | 'medium' | 'high' | 'critical',
              title: alert.title,
              description: alert.description,
              date: new Date(alert.trigger_date).toLocaleDateString(),
            }))}
          />
        </TabsContent>

        <TabsContent value="conferences" className="space-y-6">
          <ConferencePreparation
            studentId={student.id}
            upcomingConferences={dashboardData.upcoming_conferences.map(
              conf => {
                const teacher = dashboardData.teacher_insights.find(
                  insight => insight.teacher_id === conf.teacher_id
                );
                return {
                  id: conf.conference_id,
                  teacherName: teacher?.teacher_name || 'Teacher unavailable',
                  subject: teacher?.subject_area || 'Subject unavailable',
                  date: conf.date,
                  type: conf.type,
                };
              }
            )}
          />
        </TabsContent>

        <TabsContent value="insights" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Learning Analytics</CardTitle>
                <CardDescription>
                  Deep insights into your child's learning patterns
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Success Factors</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {dashboardData.progress_indicators.success_factors.map(
                        factor => (
                          <li key={factor}>• {factor}</li>
                        )
                      )}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Improvement Areas</h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {dashboardData.progress_indicators.improvement_areas.map(
                        area => (
                          <li key={area}>• {area}</li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Home-School Connection</CardTitle>
                <CardDescription>
                  Strategies to support learning at home
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">
                      Recommended Home Strategies
                    </h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      {dashboardData.learning_recommendations.map(
                        recommendation => (
                          <li key={recommendation}>• {recommendation}</li>
                        )
                      )}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Data source</h4>
                    <p className="text-sm text-gray-600">
                      Recommendations shown here come from the student insight
                      service and update when new school data is available.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
